<template>
  <div class="modal-full">
    <div class="sm-modal-parent center-block">
      <div class="modal-wrapper overflow-x-hidden overflow-y-auto inset-0 z-50 outline-none focus:outline-none justify-center items-center flex">
        <div class="modal-container relative w-auto my-6 mx-auto max-w-3xl">
          <!-- content -->
          <div class="modal-content border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            <!-- close button -->
            <button
              @click="close"
              class="absolute top-6 right-6 text-gray-500 hover:text-gray-700 text-2xl z-10">
              ✕
            </button>

            <!-- header with image -->
            <div class="relative h-64 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
              <div
                :style="{
                  backgroundImage: course.image
                    ? `url(${course.image})`
                    : 'url(/assets/images/download.jpeg)',
                }"
                class="w-full h-full bg-contain bg-center bg-no-repeat">
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-6">
                <div class="text-white">
                  <h1 class="text-3xl font-bold">{{ course.title }}</h1>
                </div>
              </div>
              <div v-if="course.price > 0" class="absolute top-6 left-6 bg-emerald-500 text-white px-4 py-2 rounded-full text-lg font-bold shadow-lg">
                {{ course.price }} ₸
              </div>
            </div>

            <!-- body -->
            <div class="p-8 overflow-y-auto" style="max-height: 60vh">
              <!-- Course info grid -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 pb-6 border-b border-gray-200">
                <div v-if="course.teacher" class="flex items-start gap-3">
                  <div class="text-2xl">👨‍🏫</div>
                  <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wide font-semibold">Преподаватель</p>
                    <p class="text-sm font-semibold text-gray-800">{{ course.teacher }}</p>
                  </div>
                </div>

                <div class="flex items-start gap-3">
                  <div class="text-2xl">📚</div>
                  <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wide font-semibold">Формат обучения</p>
                    <p class="text-sm font-semibold text-gray-800">{{ course.course_type?.val || 'N/A' }}</p>
                  </div>
                </div>

                <div class="flex items-start gap-3">
                  <div class="text-2xl">💺</div>
                  <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wide font-semibold">Доступно мест</p>
                    <p class="text-sm font-semibold text-gray-800">{{ course.max_amount }}</p>
                  </div>
                </div>

                <div v-if="course.hours" class="flex items-start gap-3">
                  <div class="text-2xl">⏱️</div>
                  <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wide font-semibold">Продолжительность</p>
                    <p class="text-sm font-semibold text-gray-800">{{ course.hours }} часов</p>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="mb-8">
                <h2 class="text-xl font-bold text-gray-800 mb-3">Описание</h2>
                <p class="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{{ course.description }}</p>
              </div>

              <!-- Course content -->
              <div v-if="course.content" class="mb-8">
                <h2 class="text-xl font-bold text-gray-800 mb-4">Программа курса</h2>
                <div
                  v-html="course.content"
                  class="prose prose-sm max-w-none text-gray-700 leading-relaxed
                    prose-headings:text-gray-800 prose-headings:font-bold prose-headings:text-base
                    prose-p:mb-3 prose-li:mb-1 prose-li:text-sm
                    prose-strong:text-gray-900 prose-strong:font-bold
                    prose-a:text-emerald-600 prose-a:hover:text-emerald-700
                    prose-ul:pl-4 prose-ol:pl-4">
                </div>
              </div>
            </div>

            <!-- footer -->
            <div class="flex gap-3 p-6 border-t border-gray-200">
              <button
                @click="submitApplication"
                class="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold py-3 px-6 rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg text-center uppercase tracking-wide">
                Оставить заявку
              </button>
              <button
                @click="close"
                class="flex-1 bg-gray-200 text-gray-800 font-bold py-3 px-6 rounded-lg hover:bg-gray-300 transition-all duration-200 text-center uppercase tracking-wide">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_CLIENT, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'CourseDetailModal',
  props: {
    courseUUID: {
      type: String,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
    onSubmit: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      course: {
        title: '',
        image: null,
        price: 0,
        teacher: '',
        course_type: { val: '' },
        max_amount: 0,
        hours: 0,
        description: '',
        content: '',
      },
      loading: true,
      error: null,
    };
  },
  mounted() {
    this.fetchCourseDetail();
  },
  methods: {
    async fetchCourseDetail() {
      try {
        this.loading = true;
        this.error = null;

        const response = await axios.get(
          `${API_CLIENT.courses}${this.courseUUID}/`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data.code === 0 || response.status === 200) {
          this.course = response.data.data;
        } else {
          this.course = response.data.data;
        }
      } catch (error) {
        console.error('Ошибка загрузки курса:', error);
        this.error = 'Не удалось загрузить данные курса';
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.onClose();
    },
    submitApplication() {
      this.onSubmit();
    },
  },
};
</script>

<style scoped>
.prose {
  font-size: 0.875rem;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.prose p {
  margin-bottom: 0.75em;
}

.prose ul,
.prose ol {
  margin-bottom: 0.75em;
}

.prose li {
  margin-bottom: 0.25em;
}

.prose strong {
  font-weight: 700;
  color: #1f2937;
}

.prose a {
  color: #059669;
  text-decoration: none;
}

.prose a:hover {
  color: #047857;
  text-decoration: underline;
}
</style>
